/**
 * StaffTrainingPage - Supervisor view of all staff training compliance.
 *
 * Uses TabulatorPrimitiveManager with backend-filtered training tasks.
 * Shows training tasks across all staff members for supervisors/managers.
 *
 * DRR-141 pattern: Employee filter (All / My Training / Employee) mirrors
 * the Subject filter pattern on TaskListPage but for HUMAN actors.
 *
 * Legislation backing (MN ALF):
 * - MN 144G.45 Subd 3: 8 hrs/year annual training, 6 mandatory topics
 * - MN 144G.72: Dementia-specific training for memory care
 * - MN 626.557: Mandatory reporter training
 * - OSHA 1910.1030: Bloodborne pathogen training
 *
 * Data sources:
 * - Uses listTrainingTasks query (Valkey index-backed)
 * - Uses getTrainingTasksByEmployee for employee-specific view
 * - Task groups: tg-0-2-*, tg-4-9-*, tg-4-13-*, tg-5-4-*
 *
 * Route: /ferp-core/training
 * Scope: training:manage (supervisors/managers only)
 */

import { useMemo, useState, useEffect } from 'react';
import {
  Container,
  Group,
  Stack,
  Title,
  ThemeIcon,
  SegmentedControl,
  Text,
} from '@mantine/core';
import { IconSchool } from '@tabler/icons-react';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useResolveActorName } from '@/contexts/ActorNameContext';
import {
  useListTrainingTasksQuery,
  useGetTasksByActorQuery,
} from '@/generated/graphql';
import {
  useEmployeeScope,
  EmployeeFilterHeader,
} from '@ferp/workflow-forms';

// Training-specific dimension labels (overrides backend defaults)
// For training: subject = trainee (not resident), performer = trainee (not caregiver)
const TRAINING_DIMENSION_LABELS: Record<string, string> = {
  subject_actor: 'By Staff Member',
  assigned_actor: 'By Trainee',
};

/** Training group prefixes for client-side filtering of My Training */
const TRAINING_PREFIXES = ['tg-0-2-', 'tg-4-9-', 'tg-4-13-', 'tg-5-4-'];

/** DRR-178: Group by dimensions for training */
const TRAINING_GROUP_DIMENSIONS = [
  { value: 'taskGroupRef', label: 'Category' },
  { value: 'status', label: 'Status' },
] as const;

export function StaffTrainingPage() {
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const resolveActorName = useResolveActorName();

  // Employee scope — filter state, employee dropdown
  const scope = useEmployeeScope({ nameResolver: resolveActorName });
  const { filter, selectedEmployeeId } = scope;

  // Group by dimension (persisted to localStorage)
  const [selectedDimension, setSelectedDimension] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ferp.training.groupBy') ?? 'taskGroupRef';
    }
    return 'taskGroupRef';
  });

  // Persist dimension selection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('ferp.training.groupBy', selectedDimension);
    }
  }, [selectedDimension]);

  // "All" — backend resolver filters training tasks via Valkey indexes
  const {
    data: allData,
    loading: allLoading,
    error: allError,
    refetch: allRefetch,
  } = useListTrainingTasksQuery({
    variables: { limit: 200 },
    skip: filter !== 'all',
    errorPolicy: 'all',
  });

  // "My Training" — tasks assigned to current user, filtered to training groups
  const {
    data: myData,
    loading: myLoading,
    error: myError,
    refetch: myRefetch,
  } = useGetTasksByActorQuery({
    variables: { actorId: actorId ?? '' },
    skip: filter !== 'my' || !actorId,
  });

  // "Employee" — training tasks for selected employee (filtered client-side)
  // TODO: Add backend getTrainingTasksByEmployee resolver for server-side filtering
  const {
    data: employeeData,
    loading: employeeLoading,
    error: employeeError,
    refetch: employeeRefetch,
  } = useGetTasksByActorQuery({
    variables: { actorId: selectedEmployeeId ?? '' },
    skip: filter !== 'employee' || !selectedEmployeeId,
  });

  // Build override data based on current filter
  const activeOverride = useMemo(() => {
    if (filter === 'all') {
      return {
        items: (allData?.listTrainingTasks?.items ?? null) as PrimitiveItem[] | null,
        loading: allLoading,
        error: allError,
        refetch: allRefetch,
      };
    }
    if (filter === 'my') {
      // Filter my tasks to training groups only
      const myTasks = myData?.getTasksByActor ?? [];
      const trainingTasks = myTasks.filter((t) =>
        TRAINING_PREFIXES.some((p) => t.taskGroupRef?.startsWith(p))
      );
      return {
        items: trainingTasks as PrimitiveItem[],
        loading: myLoading || actorLoading,
        error: myError,
        refetch: myRefetch,
      };
    }
    if (filter === 'employee' && selectedEmployeeId) {
      // Filter employee tasks to training groups only (client-side until backend resolver exists)
      const employeeTasks = employeeData?.getTasksByActor ?? [];
      const trainingTasks = employeeTasks.filter((t) =>
        TRAINING_PREFIXES.some((p) => t.taskGroupRef?.startsWith(p))
      );
      return {
        items: trainingTasks as PrimitiveItem[],
        loading: employeeLoading,
        error: employeeError,
        refetch: employeeRefetch,
      };
    }
    return undefined;
  }, [
    filter, selectedEmployeeId,
    allData, allLoading, allError, allRefetch,
    myData, myLoading, myError, myRefetch, actorLoading,
    employeeData, employeeLoading, employeeError, employeeRefetch,
  ]);

  const headerSlot = (
    <Stack gap="xs">
      <EmployeeFilterHeader
        scope={scope}
        actorName={actorName}
        actorLoading={actorLoading}
        primitiveLabel="Training"
      />
      {/* Group by dimension picker */}
      <Group gap="xs">
        <Text size="sm" c="dimmed">Group by:</Text>
        <SegmentedControl
          size="xs"
          data={TRAINING_GROUP_DIMENSIONS.map(d => ({ value: d.value, label: d.label }))}
          value={selectedDimension}
          onChange={setSelectedDimension}
        />
      </Group>
    </Stack>
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="grape">
              <IconSchool size={20} />
            </ThemeIcon>
            <Title order={2}>Staff Training</Title>
          </Group>
        </Group>

        {/* DRY CRUD template with employee-filtered training data */}
        <TabulatorPrimitiveManager
          primitiveType="task"
          headerSlot={headerSlot}
          overrideData={activeOverride}
          groupBy={selectedDimension}
          defaultViewMode="table"
          dimensionLabelOverrides={TRAINING_DIMENSION_LABELS}
        />
      </Stack>
    </Container>
  );
}
